import { usePerformanceOverview } from '../hooks/use-performance-overview';

import type { PerformanceOverview } from '@/entities/insight/endpoint';
import { type SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { SectionHeader } from '@/shared/components/section-header';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { formatNumber } from '@/shared/utils/format-number';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';
import { StatCard } from '../../shared/ui/stat-card';

export function PerformanceOverview({ fetchEnabled }: { fetchEnabled: boolean }) {
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(selectedQuery.id);
  const { data: performance, isLoading } = usePerformanceOverview({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    queryId: selectedQuery.id,
    enabled: fetchEnabled,
    publicFilter: publicFilter,
  });

  const statistic = getStatistic(performance);
  const platformStatistic = platformReducer(performance);

  const cards = [
    {
      title: 'Total volume',
      value: performance ? formatNumber(statistic.totalVolume) : '--',
      rawValue: statistic.totalVolume || 0,
      platformStatistic: platformStatistic.volume,
    },
    {
      title: 'View',
      value: performance ? formatNumber(statistic.totalViews) : '--',
      rawValue: statistic.totalViews || 0,
      platformStatistic: platformStatistic.views,
    },
    {
      title: 'Total engagement',
      value: performance ? formatNumber(statistic.totalEngagements) : '--',
      rawValue: statistic.totalEngagements || 0,
      platformStatistic: platformStatistic.engagements,
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Performance Overview"
        description="View key metrics regarding performance"
      />

      <div className="grid gap-4 px-6 py-6 lg:grid-cols-3">
        {cards.map((card) => (
          <StatCard
            key={card.title}
            title={card.title}
            value={card.rawValue}
            platformStatistic={card.platformStatistic}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  );
}

const getStatistic = (
  performance: PerformanceOverview[] | undefined,
): {
  totalVolume: number;
  totalViews: number;
  totalEngagements: number;
} => {
  if (!performance) {
    return {
      totalVolume: 0,
      totalViews: 0,
      totalEngagements: 0,
    };
  }
  return {
    totalVolume: performance.reduce((acc, curr) => acc + curr.count, 0),
    totalViews: performance.reduce((acc, curr) => acc + curr.view, 0),
    totalEngagements: performance.reduce(
      (acc, curr) => acc + curr.like + curr.comment + curr.shared,
      0,
    ),
  };
};

const platformReducer = (performance: PerformanceOverview[] | undefined) => {
  const result: {
    volume: { platform: SOURCE_PLATFORM_TYPE; value: number }[];
    views: { platform: SOURCE_PLATFORM_TYPE; value: number }[];
    engagements: { platform: SOURCE_PLATFORM_TYPE; value: number }[];
  } = {
    volume: [],
    views: [],
    engagements: [],
  };
  if (!performance) {
    return result;
  }
  performance.forEach((item) => {
    result.volume.push({ platform: item.platform, value: item.count });
    result.views.push({ platform: item.platform, value: item.view });
    result.engagements.push({
      platform: item.platform,
      value: item.like + item.comment + item.shared,
    });
  });
  return result;
};
